export type StatusColorClass =
  | 'status-badge-yellow'
  | 'status-badge-orange'
  | 'status-badge-green'
  | 'status-badge-red'
  | 'status-badge-blue'
  | 'status-badge-grey'
  | 'status-badge-purple'
  | 'status-badge';

export type StatusMatchType = 'contains' | 'exact' | 'prefix';

export interface StatusColorRule {
  match: StatusMatchType;
  pattern: string;        // lowercase
  color: StatusColorClass;
}

function normalizeStatusText(value: string): string {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/\s*\+\s*/g, ' + ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Single ordered rules array — evaluated top-to-bottom, first match wins.
 * Group by priority: contains → exact → prefix.
 */
export const STATUS_COLOR_RULES: StatusColorRule[] = [
  // ── Exact matrix from status table ──
  // Gray
  { match: 'exact', pattern: 'in progress, not submitted', color: 'status-badge-grey' },
  { match: 'exact', pattern: 'reversed', color: 'status-badge-grey' },
  { match: 'exact', pattern: 'invalid', color: 'status-badge-grey' },
  { match: 'exact', pattern: 'Archived', color: 'status-badge-grey' },
   { match: 'exact', pattern: 'Previous', color: 'status-badge-grey' },
  { match: 'exact', pattern: 'Other', color: 'status-badge-grey' },
  { match: 'exact', pattern: 'Current', color: 'status-badge-grey' },

  // Yellow
  { match: 'exact', pattern: 'backoffice pending', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'intake pending', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'Pending', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'Initiated', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'intake pending + bg pending', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'intake pending + bg approved', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'analyst pending', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'analyst pending + bg pending', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'analyst pending + bg approved', color: 'status-badge-yellow' },

  // Orange
  { match: 'exact', pattern: 'deficiency pending', color: 'status-badge-orange' },
  { match: 'exact', pattern: 'deficiency pending + bg pending', color: 'status-badge-orange' },
  { match: 'exact', pattern: 'deficiency pending + bg approved', color: 'status-badge-orange' },
  { match: 'exact', pattern: 'analyst denied', color: 'status-badge-orange' },
  { match: 'exact', pattern: 'analyst denied + bg pending', color: 'status-badge-orange' },
  { match: 'exact', pattern: 'analyst denied + bg approved', color: 'status-badge-orange' },

  // Blue
  { match: 'exact', pattern: 'created', color: 'status-badge-blue' },
  { match: 'exact', pattern: 'Insert', color: 'status-badge-blue' },
  { match: 'exact', pattern: 'partially approved', color: 'status-badge-blue' },
  { match: 'exact', pattern: 'partially approved + bg pending', color: 'status-badge-blue' },
  { match: 'exact', pattern: 'partially approved + bg approved', color: 'status-badge-blue' },
  { match: 'exact', pattern: 'analyst approved + bg pending', color: 'status-badge-blue' },

  // Green
    { match: 'exact', pattern: 'Received', color: 'status-badge-green' },
  { match: 'exact', pattern: 'approved', color: 'status-badge-green' },
  { match: 'exact', pattern: 'Acceptable', color: 'status-badge-green' },
  { match: 'exact', pattern: 'Eligible', color: 'status-badge-green' },
  { match: 'exact', pattern: 'Delivered', color: 'status-badge-green' },
  { match: 'prefix', pattern: 'Case', color: 'status-badge-green' },
  { match: 'exact', pattern: 'Complete', color: 'status-badge-green' },
  { match: 'exact', pattern: 'Completed', color: 'status-badge-green' },
  { match: 'exact', pattern: 'Verified', color: 'status-badge-green' },
  { match: 'exact', pattern: 'UPDATE', color: 'status-badge-green' },
  { match: 'exact', pattern: 'Updated', color: 'status-badge-green' },
  { match: 'exact', pattern: 'analyst approved', color: 'status-badge-green' },
  { match: 'exact', pattern: 'analyst approved + bg approved', color: 'status-badge-green' },

  // Red
  { match: 'exact', pattern: 'intake pending + bg denied', color: 'status-badge-red' },
  { match: 'exact', pattern: 'Failed', color: 'status-badge-red' },
  { match: 'exact', pattern: 'Past Due', color: 'status-badge-red' },
  { match: 'exact', pattern: 'Not Eligible', color: 'status-badge-red' },
  { match: 'exact', pattern: 'deficiency pending + bg denied', color: 'status-badge-red' },
  { match: 'exact', pattern: 'analyst pending + bg denied', color: 'status-badge-red' },
  { match: 'exact', pattern: 'analyst  pending + bg denied', color: 'status-badge-red' },
  { match: 'exact', pattern: 'analyst denied + bg denied', color: 'status-badge-red' },
  { match: 'exact', pattern: 'partially approved + bg denied', color: 'status-badge-red' },
  { match: 'exact', pattern: 'analyst approved + bg denied', color: 'status-badge-red' },
  { match: 'exact', pattern: 'DUPLICATE_FILE_ERROR', color: 'status-badge-red' },
  { match: 'exact', pattern: 'DELETE', color: 'status-badge-red' },

  // Additional supported statuses in current flows
  { match: 'exact', pattern: 'pending', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'submitted', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'surrender requested', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'dnq', color: 'status-badge-orange' },
  { match: 'exact', pattern: 'assigned', color: 'status-badge-orange' },
  { match: 'exact', pattern: 'requested', color: 'status-badge-orange' },
  { match: 'exact', pattern: 'active', color: 'status-badge-green' },
  { match: 'exact', pattern: 'authorized', color: 'status-badge-green' },
  { match: 'exact', pattern: 'resolved', color: 'status-badge-green' },
  { match: 'exact', pattern: 'clear', color: 'status-badge-green' },
  { match: 'exact', pattern: 'closed - action taken', color: 'status-badge-green' },
  { match: 'exact', pattern: 'closed - unfounded', color: 'status-badge-green' },
  { match: 'exact', pattern: 'declined', color: 'status-badge-red' },
    { match: 'exact', pattern: 'Declined', color: 'status-badge-red' },
  { match: 'exact', pattern: 'inactive', color: 'status-badge-red' },
  { match: 'exact', pattern: 'denied', color: 'status-badge-red' },
  { match: 'exact', pattern: 'incomplete', color: 'status-badge-red' },
  { match: 'exact', pattern: 'state board', color: 'status-badge-red' },
  { match: 'exact', pattern: 'ineligible', color: 'status-badge-red' },

  // ── Prefix fallbacks for API variants/suffixes ──
  { match: 'prefix', pattern: 'analyst approved + bg approved', color: 'status-badge-green' },
  { match: 'prefix', pattern: 'analyst approved + bg pending', color: 'status-badge-blue' },
  { match: 'prefix', pattern: 'analyst approved + bg denied', color: 'status-badge-red' },
  { match: 'prefix', pattern: 'partially approved + bg approved', color: 'status-badge-blue' },
  { match: 'prefix', pattern: 'partially approved + bg pending', color: 'status-badge-blue' },
  { match: 'prefix', pattern: 'partially approved + bg denied', color: 'status-badge-red' },
  { match: 'prefix', pattern: 'analyst denied + bg approved', color: 'status-badge-orange' },
  { match: 'prefix', pattern: 'analyst denied + bg pending', color: 'status-badge-orange' },
  { match: 'prefix', pattern: 'analyst denied + bg denied', color: 'status-badge-red' },
  { match: 'prefix', pattern: 'deficiency pending + bg approved', color: 'status-badge-orange' },
  { match: 'prefix', pattern: 'deficiency pending + bg pending', color: 'status-badge-orange' },
  { match: 'prefix', pattern: 'deficiency pending + bg denied', color: 'status-badge-red' },
  { match: 'prefix', pattern: 'intake pending + bg approved', color: 'status-badge-yellow' },
  { match: 'prefix', pattern: 'intake pending + bg pending', color: 'status-badge-yellow' },
  { match: 'prefix', pattern: 'intake pending + bg denied', color: 'status-badge-red' },
  { match: 'prefix', pattern: 'analyst pending + bg approved', color: 'status-badge-yellow' },
  { match: 'prefix', pattern: 'analyst pending + bg pending', color: 'status-badge-yellow' },
  { match: 'prefix', pattern: 'analyst pending + bg denied', color: 'status-badge-red' },
  { match: 'prefix', pattern: 'partially approved', color: 'status-badge-blue' },
  { match: 'prefix', pattern: 'analyst approved', color: 'status-badge-green' },
  { match: 'prefix', pattern: 'deficiency pending', color: 'status-badge-orange' },
  { match: 'prefix', pattern: 'analyst denied', color: 'status-badge-orange' },
  { match: 'prefix', pattern: 'backoffice', color: 'status-badge-yellow' },
  { match: 'prefix', pattern: 'intake pending', color: 'status-badge-yellow' },
  { match: 'prefix', pattern: 'analyst pending', color: 'status-badge-yellow' },
  { match: 'prefix', pattern: 'analyst  pending', color: 'status-badge-yellow' },
];

/** Default color for unmatched application statuses */
export const STATUS_DEFAULT_COLOR: StatusColorClass = 'status-badge';

/** Resolve color class from a status string using the single rules array */
export function resolveStatusColor(status: string | null | undefined): StatusColorClass {
  if (!status) return 'status-badge-grey';
  const s = normalizeStatusText(status);
  for (const rule of STATUS_COLOR_RULES) {
    const pattern = normalizeStatusText(rule.pattern);
    switch (rule.match) {
      case 'contains': if (s.includes(pattern)) return rule.color; break;
      case 'exact':    if (s === pattern) return rule.color; break;
      case 'prefix':   if (s.startsWith(pattern)) return rule.color; break;
    }
  }
  return STATUS_DEFAULT_COLOR;
}

/** Background check status rules */
export const BG_STATUS_COLOR_RULES: StatusColorRule[] = [
  { match: 'exact', pattern: 'pending', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'card over 1 year', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'information requested', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'in process', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'issued', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'manual', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'record found', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'reprint', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'resubmission', color: 'status-badge-yellow' },
  { match: 'exact', pattern: 'clear', color: 'status-badge-green' },
  { match: 'exact', pattern: 'denied', color: 'status-badge-red' },
];

/** Default color for unmatched BG statuses (Pending, Card Over 1 Year, etc.) */
export const BG_STATUS_DEFAULT_COLOR: StatusColorClass = 'status-badge-yellow';

/** Resolve color class from a background-check status string */
export function resolveBgStatusColor(status: string | null | undefined): StatusColorClass {
  if (!status) return 'status-badge-grey';
  const s = normalizeStatusText(status);
  for (const rule of BG_STATUS_COLOR_RULES) {
    if (s === normalizeStatusText(rule.pattern)) return rule.color;
  }
  return BG_STATUS_DEFAULT_COLOR;
}
